import React, { lazy, Suspense } from "react";
import { createRoot } from "react-dom/client";
import { I18nProvider } from "./i18n";
import { Header } from "./components/Header";
import { Hero } from "./components/Hero";
import { Stats } from "./components/Stats";
import { HowItWorks } from "./components/HowItWorks";
import { Features } from "./components/Features";
import { Integrations } from "./components/Integrations";
import { UseCases } from "./components/UseCases";
import { Testimonials } from "./components/Testimonials";
import { Pricing } from "./components/Pricing";
import { Affiliate } from "./components/Affiliate";
import { BlogPreview } from "./components/BlogPreview";
import { CTA } from "./components/CTA";
import { Footer } from "./components/Footer";

const About = lazy(() => import("./pages/About").then(m => ({ default: m.About })));
const Privacy = lazy(() => import("./pages/Privacy").then(m => ({ default: m.Privacy })));
const Terms = lazy(() => import("./pages/Terms").then(m => ({ default: m.Terms })));
const Status = lazy(() => import("./pages/Status").then(m => ({ default: m.Status })));
const PricingPage = lazy(() => import("./pages/PricingPage").then(m => ({ default: m.PricingPage })));
const FeaturesPage = lazy(() => import("./pages/FeaturesPage").then(m => ({ default: m.FeaturesPage })));
const ChatbotBuilderPage = lazy(() => import("./pages/features/ChatbotBuilderPage").then(m => ({ default: m.ChatbotBuilderPage })));
const NoCodePage = lazy(() => import("./pages/features/NoCodePage").then(m => ({ default: m.NoCodePage })));
const IntegrationsPage = lazy(() => import("./pages/features/IntegrationsPage").then(m => ({ default: m.IntegrationsPage })));
const MultilingualPage = lazy(() => import("./pages/features/MultilingualPage").then(m => ({ default: m.MultilingualPage })));
const SignupPage = lazy(() => import("./pages/SignupPage").then(m => ({ default: m.SignupPage })));
const EcommercePage = lazy(() => import("./pages/solutions/EcommercePage").then(m => ({ default: m.EcommercePage })));
const RestaurantsPage = lazy(() => import("./pages/solutions/RestaurantsPage").then(m => ({ default: m.RestaurantsPage })));
const HealthcarePage = lazy(() => import("./pages/solutions/HealthcarePage").then(m => ({ default: m.HealthcarePage })));
const EducationPage = lazy(() => import("./pages/solutions/EducationPage").then(m => ({ default: m.EducationPage })));

function Landing() {
  return (
    <div className="min-h-screen bg-[#0a0a12]">
      <Header />
      <main>
        <Hero />
        <Stats />
        <HowItWorks />
        <Features />
        <Integrations />
        <UseCases />
        <Testimonials />
        <Pricing />
        <Affiliate />
        <BlogPreview />
        <CTA />
      </main>
      <Footer />
    </div>
  );
}

const Loading = () => (
  <div className="min-h-screen bg-[#0a0a12] flex items-center justify-center">
    <div className="text-[#8a7a9a]">Loading...</div>
  </div>
);

function App() {
  const path = window.location.pathname;

  let page: React.ReactNode;
  switch (path) {
    case "/about":
      page = <Suspense fallback={<Loading />}><About /></Suspense>;
      break;
    case "/privacy":
      page = <Suspense fallback={<Loading />}><Privacy /></Suspense>;
      break;
    case "/terms":
      page = <Suspense fallback={<Loading />}><Terms /></Suspense>;
      break;
    case "/status":
      page = <Suspense fallback={<Loading />}><Status /></Suspense>;
      break;
    case "/pricing":
      page = <Suspense fallback={<Loading />}><PricingPage /></Suspense>;
      break;
    case "/features":
      page = <Suspense fallback={<Loading />}><FeaturesPage /></Suspense>;
      break;
    case "/features/chatbot-builder":
      page = <Suspense fallback={<Loading />}><ChatbotBuilderPage /></Suspense>;
      break;
    case "/features/no-code":
      page = <Suspense fallback={<Loading />}><NoCodePage /></Suspense>;
      break;
    case "/features/integrations":
      page = <Suspense fallback={<Loading />}><IntegrationsPage /></Suspense>;
      break;
    case "/features/multilingual":
      page = <Suspense fallback={<Loading />}><MultilingualPage /></Suspense>;
      break;
    case "/signup":
      page = <Suspense fallback={<Loading />}><SignupPage /></Suspense>;
      break;
    case "/solutions/ecommerce":
      page = <Suspense fallback={<Loading />}><EcommercePage /></Suspense>;
      break;
    case "/solutions/restaurants":
      page = <Suspense fallback={<Loading />}><RestaurantsPage /></Suspense>;
      break;
    case "/solutions/healthcare":
      page = <Suspense fallback={<Loading />}><HealthcarePage /></Suspense>;
      break;
    case "/solutions/education":
      page = <Suspense fallback={<Loading />}><EducationPage /></Suspense>;
      break;
    default:
      page = <Landing />;
      break;
  }

  return <I18nProvider>{page}</I18nProvider>;
}

const root = createRoot(document.getElementById("root")!);
root.render(<App />);
